/**
 * CL-DISC-02 §G — what a buyer sees of a discount: a "% OFF" badge, the regular price struck
 * through, and (on the listing page) one urgency line. The sale price itself is NOT rendered
 * here — it is `listing.attributes.price`, which the card and the order panel already show.
 *
 * The urgency line is its OWN named export, `DiscountUrgency`, rather than part of the default
 * component. Two reasons, both from fix round 1:
 *   1. Label adjacency: `RegularPriceStruck`'s visually-hidden "Sale price" label exists to
 *      announce the value that immediately follows it — the live sale price. If urgency sat
 *      between that label and the price (as it did when the default component rendered badge +
 *      struck price + urgency as one block ahead of the price paragraph), a screen reader would
 *      read "…Sale price, Sale ends in 3 days, $55.00" — the label would be severed from its
 *      value by an unrelated sentence.
 *   2. Spec §G says the urgency line goes BENEATH the price, not above it: `.urgency` has
 *      `flex-basis: 100%`, so bundling it into the same flex root as badge + struck price made
 *      it wrap onto a line ABOVE the (separately rendered) live price instead of below it.
 * So the default `ListingDiscountPrice` renders only the badge and the struck regular price
 * (ending on the hidden "Sale price" label, immediately followed by the caller's own price
 * markup), and callers place `DiscountUrgency` as a separate, later sibling — after the price,
 * not before it. See OrderPanel.js's `PriceMaybe` for the two-hook shape this produces.
 *
 * Imported by relative path from ListingCard and OrderPanel, deliberately not via
 * components/index.js, whose insertion order affects the CSS bundle.
 *
 * A11y: a strikethrough carries no meaning to a screen reader, so both prices get a visually
 * hidden label. Nothing here reads the clock unless `nowMs` is omitted.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { readListingDiscount, discountUrgency } from '../../util/custom/listingDiscountRules';

import css from './ListingDiscountPrice.module.css';

const { Money } = sdkTypes;

export const DiscountBadge = ({ publicData, className }) => {
  const d = readListingDiscount(publicData);
  if (!d) return null;
  return (
    <span className={classNames(css.badge, className)}>
      <FormattedMessage id="ListingDiscountPrice.badge" values={{ percentage: d.percentage }} />
    </span>
  );
};

export const RegularPriceStruck = ({ publicData, currency, intl, className }) => {
  const d = readListingDiscount(publicData);
  if (!d) return null;
  return (
    <span className={classNames(css.regular, className)}>
      <span className={css.srOnly}>
        <FormattedMessage id="ListingDiscountPrice.regularPriceLabel" />
      </span>
      <s className={css.struck}>
        {formatMoney(intl, new Money(d.originalPriceInSubunits, currency))}
      </s>
      <span className={css.srOnly}>
        <FormattedMessage id="ListingDiscountPrice.salePriceLabel" />
      </span>
    </span>
  );
};

const UrgencyText = ({ discount, nowMs }) => {
  const u = discountUrgency(discount, nowMs);
  if (!u) return null;
  if (u.kind === 'uses') {
    return (
      <FormattedMessage id="ListingDiscountPrice.usesLeft" values={{ usesLeft: u.usesLeft }} />
    );
  }
  if (u.daysLeft === 0) return <FormattedMessage id="ListingDiscountPrice.endsToday" />;
  if (u.daysLeft === 1) return <FormattedMessage id="ListingDiscountPrice.endsTomorrow" />;
  return <FormattedMessage id="ListingDiscountPrice.endsInDays" values={{ days: u.daysLeft }} />;
};

/**
 * The one urgency line (spec §G), rendered as its own sibling BENEATH the price — never bundled
 * with the badge/struck-price block. Null without a valid discount.
 */
export const DiscountUrgency = ({ publicData, nowMs = Date.now(), className }) => {
  const d = readListingDiscount(publicData);
  if (!d) return null;
  return (
    <p className={classNames(css.urgency, className)}>
      <UrgencyText discount={d} nowMs={nowMs} />
    </p>
  );
};

const ListingDiscountPrice = props => {
  const { publicData, currency, intl, className } = props;
  const d = readListingDiscount(publicData);
  if (!d) return null;
  return (
    <div className={classNames(css.root, className)}>
      <DiscountBadge publicData={publicData} />
      <RegularPriceStruck publicData={publicData} currency={currency} intl={intl} />
    </div>
  );
};

export default ListingDiscountPrice;
